import { AnalysisRun, AnalysisRunService } from "../services/analysis-run.service";
import { AnalysisError, bounded } from "../utils/analysis-async";
import { EvaluationFlowsEnum, NodeData, ResultInfo } from "../models/shared.model";
import { Inject, Injector } from "@angular/core";
import { DomainService } from "../services/domain.service";
import { MessageService } from "../services/message.service";
import { ConfigService } from "../services/config.service";
import { AttachmentService } from "../services/attachment.service";
import { LinksService } from "../services/links.service";
import { SharedService } from "../services/shared.service";
import { StepResult } from "../models/step-result";
import { RiskLevel } from "../models/risk-level";
import { FlowNodeNamesEnum } from "../models/flow";
import { NodeResultDecisions } from "../models/constants";
import { ImageService } from "../services/image.service";
import { QRCodeService } from "../services/qrCode.service";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";

export abstract class StepProcess {
  protected readonly run: AnalysisRun;
  nodeLogId: number;

  protected domainService: DomainService;
  protected messageService: MessageService;
  protected configService: ConfigService;
  protected attachmentService: AttachmentService;
  protected linksService: LinksService;
  protected sharedService: SharedService;
  protected imageService: ImageService;
  protected qrCodeService: QRCodeService;
  protected translate: TranslateService;
  protected http: HttpClient;

  constructor(
    @Inject(Injector) protected injector: Injector
  ) {
    this.run = this.injector.get(AnalysisRunService).current;
    this.domainService = this.injector.get(DomainService);
    this.messageService = this.injector.get(MessageService);
    this.configService = this.injector.get(ConfigService);
    this.attachmentService = this.injector.get(AttachmentService);
    this.linksService = this.injector.get(LinksService);
    this.sharedService = this.injector.get(SharedService);
    this.imageService = this.injector.get(ImageService);
    this.qrCodeService = this.injector.get(QRCodeService);
    this.translate = this.injector.get(TranslateService);
    this.http = this.injector.get(HttpClient);
  }

  process: (data?: NodeData, info?: {}) => any;

  async callWithTimeout<AnalysisResult>(
    promiseFn: () => Promise<AnalysisResult>,
    info: ResultInfo,
    flowName: EvaluationFlowsEnum,
    flowNodeName: FlowNodeNamesEnum,
    timeout: number
  ): Promise<AnalysisResult | string> {
    this.run.assertActive();
    const request = Promise.resolve().then(promiseFn);
    try {
      return await bounded(() => request, timeout, flowNodeName, this.run);
    } catch (error) {
      this.run.assertActive();
      if (!(error instanceof AnalysisError) || error.code !== 'timeout') throw error;
      this.run.issue(flowName, error, `node:${this.nodeLogId}`);
      request.then(response => {
        if (!this.run.active) return;
        this.notifyResult(response, { ...info, ...response['additionalInfo'] }, flowName, flowNodeName);
      }).catch(error => {
        this.run.issue(flowName, error, `node:${this.nodeLogId}`);
      });
      return 'pending';
    }
  }

  private notifyResult(response: any, info: any, flowName: EvaluationFlowsEnum, flowNodeName: FlowNodeNamesEnum) {
    let result = null;

    switch (flowNodeName) {
      case FlowNodeNamesEnum.getDomainAge:
        result = response["level"] == RiskLevel.Low ? NodeResultDecisions.OLD :
          (response["level"] == RiskLevel.Moderate ? NodeResultDecisions.PENDING : NodeResultDecisions.NEW);
        break;

      case FlowNodeNamesEnum.isPriorContactWithSender:
        result = response["level"] == RiskLevel.Low;
        break;
    }

    if (!this.run.active) return;
    const update = new StepResult(result, response["additionalInfo"]?.info, info, flowName, flowNodeName);
    update.runId = this.run.id;
    update.nodeLogId = this.nodeLogId;
    this.sharedService.messageReceived.next(update);
  }
}
